import { render } from "preact";
import { type Signal, signal } from "@preact/signals";
import type { ClayKind } from "../clay.ts";
import styles from "./clays.module.css";

/** The choice of clay for free kneading, opened by the game. */
export interface ClayChooser {
  /**
   * Shows the choice of the clays `opened`; `choose` gets the clay chosen, `onClose` runs when
   * it is closed instead.
   */
  open(
    opened: ReadonlySet<ClayKind>,
    choose: (kind: ClayKind) => void,
    onClose: () => void,
  ): void;
  /** The way back, as 閉じる goes (null while the choice is not shown). */
  readonly wayBack: (() => void) | null;
}

/** A clay's name, as the choice shows it. */
export const clayName = (kind: ClayKind) => CLAYS.find((c) => c.kind === kind)!.name;

const CLAYS: { kind: ClayKind; name: string; holes: string }[] = [
  { kind: "blob", name: "塊", holes: "穴なし" },
  { kind: "donut", name: "ドーナツ", holes: "穴ひとつ" },
  { kind: "pretzel", name: "めがねドーナツ", holes: "穴ふたつ" },
  { kind: "trefoil", name: "みつわドーナツ", holes: "穴みっつ" },
  { kind: "quatrefoil", name: "よつわドーナツ", holes: "穴よっつ" },
];

/** Free kneading: which clay to start from, with no customer and nothing judged. */
export function mountClayChooser(root: HTMLElement): ClayChooser {
  const open = signal(false);
  const opened = signal<ReadonlySet<ClayKind>>(new Set());
  let choose: (kind: ClayKind) => void = () => {}, onClose = () => {};
  const close = () => {
    open.value = false;
    onClose();
  };
  render(
    <ChooserScreen
      open={open}
      opened={opened}
      choose={(kind) => {
        open.value = false;
        choose(kind);
      }}
      close={close}
    />,
    root,
  );
  return {
    open(clays, chosen, back) {
      opened.value = clays;
      choose = chosen;
      onClose = back;
      open.value = true;
    },
    get wayBack() {
      return open.value ? close : null;
    },
  };
}

function ChooserScreen(
  { open, opened, choose, close }: {
    open: Signal<boolean>;
    opened: Signal<ReadonlySet<ClayKind>>;
    choose: (kind: ClayKind) => void;
    close: () => void;
  },
) {
  if (!open.value) return null;
  return (
    <div class={styles.chooser}>
      <div class={styles.card}>
        <h2 class={styles.heading}>自由にこねる</h2>
        <p class={styles.lead}>
          依頼もおてほんもなしで、好きな形にこねます。できたら好きな仕上げにして、棚に飾れます。
        </p>
        <p class={styles.question}>どの粘土でこねる？</p>
        <div class={styles.clays}>
          {CLAYS.map(({ kind, name, holes }) => (
            <button
              key={kind}
              type="button"
              class={styles.clay}
              disabled={!opened.value.has(kind)}
              onClick={() => choose(kind)}
            >
              <ClayPicture kind={kind} />
              <b>{name}</b>
              <small>{opened.value.has(kind) ? holes : `${holes}の依頼をクリアで解放`}</small>
            </button>
          ))}
        </div>
        <div class={`row ${styles.actions}`}>
          <button type="button" class="reset" onClick={close}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

/**
 * A little drawing of the clay: a lump, a ring, two rings side by side, three in a triangle, or
 * four in a square.
 */
function ClayPicture({ kind }: { kind: ClayKind }) {
  return (
    <svg class={styles.picture} viewBox="0 0 64 40" aria-hidden="true">
      {kind === "blob" && <ellipse cx="32" cy="21" rx="17" ry="14" />}
      {kind === "donut" && (
        <path
          fill-rule="evenodd"
          d="M32 7a19 13 0 1 0 0 26a19 13 0 1 0 0-26ZM32 15a8 5 0 1 1 0 10a8 5 0 1 1 0-10Z"
        />
      )}
      {kind === "pretzel" && (
        <path
          fill-rule="evenodd"
          d="M21 8a16 12 0 1 0 0 24a16 12 0 0 0 11-4a16 12 0 1 0 0-16a16 12 0 0 0-11-4ZM20 16a6 4 0 1 1 0 8a6 4 0 1 1 0-8ZM44 16a6 4 0 1 1 0 8a6 4 0 1 1 0-8Z"
        />
      )}
      {kind === "trefoil" && (
        <g fill-rule="evenodd">
          <path d="M23 2a11 8 0 1 0 0 16a11 8 0 1 0 0-16ZM23 7.5a4 2.5 0 1 1 0 5a4 2.5 0 1 1 0-5Z" />
          <path d="M41 2a11 8 0 1 0 0 16a11 8 0 1 0 0-16ZM41 7.5a4 2.5 0 1 1 0 5a4 2.5 0 1 1 0-5Z" />
          <path d="M32 18a11 8 0 1 0 0 16a11 8 0 1 0 0-16ZM32 23.5a4 2.5 0 1 1 0 5a4 2.5 0 1 1 0-5Z" />
        </g>
      )}
      {kind === "quatrefoil" && (
        <g fill-rule="evenodd">
          <path d="M23 3a10 8 0 1 0 0 16a10 8 0 1 0 0-16ZM23 8.5a3.6 2.5 0 1 1 0 5a3.6 2.5 0 1 1 0-5Z" />
          <path d="M41 3a10 8 0 1 0 0 16a10 8 0 1 0 0-16ZM41 8.5a3.6 2.5 0 1 1 0 5a3.6 2.5 0 1 1 0-5Z" />
          <path d="M23 21a10 8 0 1 0 0 16a10 8 0 1 0 0-16ZM23 26.5a3.6 2.5 0 1 1 0 5a3.6 2.5 0 1 1 0-5Z" />
          <path d="M41 21a10 8 0 1 0 0 16a10 8 0 1 0 0-16ZM41 26.5a3.6 2.5 0 1 1 0 5a3.6 2.5 0 1 1 0-5Z" />
        </g>
      )}
    </svg>
  );
}
